import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { useRouteMatch } from 'react-router-dom';
import { FaFacebookSquare as IconFacebook, FaTwitterSquare as IconTwitter } from 'react-icons/fa';
import { useScreenQuery } from 'playup-web-core';

import appConfig from '../../appConfig';
import GlobalContext from '../../contexts/GlobalContext';
import USFooterNotes from './USFooterNotes';
import AUFooterNotes from './AUFooterNotes';

const openBlank = (url) => url && !url.startsWith('/');

const isUS = appConfig.regions.isUS;

const Footer = ({ hideRG }: { hideRG?: boolean }) => {
	const { isSmall } = useScreenQuery();
	const { linkGroups } = React.useContext(GlobalContext);

	const { operators, footerGroups } = React.useMemo(
		() => ({
			operators: linkGroups.find((linkGroup) => linkGroup.type.id === 1),
			footerGroups: linkGroups.filter((linkGroup) => linkGroup.type.id === 2),
		}),
		[linkGroups]
	);

	const [fantasySelected, setFantasySelected] = React.useState(false);
	const isCurrentlyOnFantasy = !!useRouteMatch('/fantasy');
	React.useEffect(() => {
		setFantasySelected(isCurrentlyOnFantasy);
	}, [isCurrentlyOnFantasy]);

	return (
		<footer className="_my-4 _w-full _pb-2">
			{!!operators && (
				<>
					<div className="_border-t _border-grey _my-4 _mx-4 _opacity-25" />
					<FooterContainer isFantasy={fantasySelected} className="_flex _justify-around _mt-2 _flex-col md:_flex-row">
						<div className="_text-center md:_ml-4">
							<div className="_uppercase _font-portal-condensed">{operators.name}</div>
							<ul className="_flex _flex-wrap _items-center _mt-4 _justify-center">
								{operators.links.map((operator) => (
									<li key={operator.id} className="_mx-2 _mb-4">
										<img
											src={isSmall ? operator.images.mobile : operator.images.desktop}
											alt={operator.name}
											className="_h-12"
										/>
									</li>
								))}
							</ul>
						</div>
					</FooterContainer>
				</>
			)}
			{footerGroups.length > 0 && (
				<>
					<div className="_border-t _border-grey _my-4 _mx-4 _opacity-25" />
					<FooterContainer
						isFantasy={fantasySelected}
						className="_flex _justify-around _mt-2 _flex-wrap _flex-col md:_flex-row _text-center md:_text-left"
					>
						{footerGroups.map((footerGroup) => (
							<div className="_mb-4 _mr-4" key={footerGroup.id}>
								<h4 className="_font-semibold _uppercase _mt-4 md:_mt-0 _font-portal-condensed">
									{footerGroup.name !== 'Social' ? footerGroup.name || '' : ''}
								</h4>
								<ul
									className={classNames(
										'_mt-0 md:_mt-4',
										footerGroup.name === 'Downloads' &&
											'_flex _flex-row _items-center _mt-4 _justify-center md:_flex-col _space-x-4 md:_space-x-0',
										footerGroup.name === 'Social' &&
											'_flex _flex-row _items-center _h-full _justify-center md:_flex-col'
									)}
								>
									{footerGroup.links.map((item) =>
										// TODO: Fix this big nested ternary!
										// eslint-disable-next-line no-nested-ternary
										item.name === 'Facebook' ? (
											<li key={item.id} className="_mb-4 _mr-4 md:_mr-0">
												<a
													href={item.url || undefined}
													target="_blank"
													title={`${appConfig.brand.name} on Facebook`}
													className="hover:_text-secondary"
													rel="noopener noreferrer"
												>
													<IconFacebook size={24} />
												</a>
											</li>
										) : item.name === 'Twitter' ? (
											<li key={item.id} className="_mb-4">
												<a
													href={item.url || undefined}
													target="_blank"
													title={`${appConfig.brand.name} on Twitter`}
													className="hover:_text-secondary"
													rel="noopener noreferrer"
												>
													<IconTwitter size={24} />
												</a>
											</li>
										) : (
											<li key={item.id} className="_mt-2">
												<a
													href={item.url || '#'}
													target={openBlank(item.url) ? '_blank' : '_self'}
													className="hover:_text-secondary"
													rel="noopener noreferrer"
												>
													{Object.keys(item.images).length > 0 ? (
														<img
															src={isSmall ? item.images.mobile : item.images.desktop}
															alt={item.name}
															className="_h-10"
														/>
													) : (
														item.name || ''
													)}
												</a>
											</li>
										)
									)}
								</ul>
							</div>
						))}
					</FooterContainer>
				</>
			)}

			<div className="_border-t _border-grey _my-4 _mx-4 _border-opacity-25" />
			{!hideRG && (isUS ? <USFooterNotes /> : <AUFooterNotes />)}

			{appConfig.portal.appVersion && <VersionText>Version {appConfig.portal.appVersion}</VersionText>}
		</footer>
	);
};
export default Footer;

const FooterContainer = styled.div<{ isFantasy: boolean }>`
	@media (min-width: 1025px) {
		margin-left: ${(props) => (props.isFantasy ? '180px' : '0')};
		margin-right: ${(props) => (props.isFantasy ? '180px' : '0')};
	}
`;

const VersionText = styled.div`
	font-size: 12px;
	color: #fff;
	text-align: right;
	margin-top: 10px;
	padding: 10px 10px 40px;
`;
